import { fn } from '../utils';
import resolveToError = fn.resolveToError;
import EncodeQueryData = fn.EncodeQueryData;
import { CompatibilityService } from './compatibilityservice';
import { Subject } from 'rxjs';

/// Angular Service "NavSettings" to Store/Load User/Global Values via JSON on backend

const _baseUserQueryUrl = "/infoma.Web.Nav/NavSettings?";
const _baseGlobalQueryUrl = "/infoma.Web.Nav/NavGlobalSettings?";

/** Interface Describing Access to a Settings Value */
export interface ISetting<T> {
    /**Load Settingsvalue  */
    LoadValue(): PromiseLike<T>;
    /**Save Settingsvalue; sync : allow for sync Execution (necessary in pageunload) */
    SaveValue(value: T, ignoreIfNotAllowed?: boolean, sync?: boolean): PromiseLike<T>;
    /** Delete SettingsValue  */
    DeleteValue(): PromiseLike<boolean>;
    /** Marker : Is Modification of Items Prevent? */
    DisallowModify?: boolean;
}

/** (Partial) Apply Utility for a Persistent Setting */
export interface IPersistentSetting<T> {
    /** Apply State underlying object for internal Use only! */
    apply: (setting: T) => void;
    /** Get State from Underlying object, for internal Use Only
    * @param {object} defaultValues returned state is not set, use this as default value (allows to keep alive not used settings in current state)
    */
    getState: (defaultValues?: any) => T;
    /** add Sub State Item with given Name, returning value for deregistration */
    addSubItem: <TSub>(name: string, setting: IPersistentItem<TSub>) => () => void;
}

/** Navision Context to Load/Store Settings etc. from */
export interface INavCustomization {
    ConfigName?: string;

    NavUrl?: string;

    /** Optional Explicit Default Lookup Settings */
    LookupSettings?: {
        MaxRead?: number;
        MinSearch?: number;
    };
}

/** Info For User/Global Settings */
export interface ISettingsInfo {
    /** Server Side Marker */
    State: any;
    /** the actual content (string or Serialized data)  */
    Content: any;
    /** Does the requested item exist */
    Exists: boolean;
    /** Is Modification of Item Allowed by current User */
    ModifyAllowed: boolean;
}

/** Settings Key can be e.g. "Benutzerschreibtisch/Suchen" or ["Benutzerschreibtisch", "Suchen"] */
export type KEY_Settings = string | string[];

/** (Partial) Apply Utility (direct/indirect) for a Persistent Setting */
export type IPersistentItem<T> = IPersistentSetting<T> | (() => IPersistentSetting<T>);

/** UserSettings Apply/Load/Save Abstraction: Subject of Self if changes take place */
export class InfUserSettingsAccess extends Subject<InfUserSettingsAccess> {

    constructor(private NavSettings: NavSettingsService, private $c: CompatibilityService, key: KEY_Settings) {
        super();
        this.settings = CreateSetting(fn.noop, fn.noop);
        this.RefreshPersistKey(key, false);
    }
    private storage: ISetting<any>;
    private isApplied: boolean;
    private readonly settings: IPersistentSetting<any>;
    private loadedValue: any;
    private initialValue: any;
    public RefreshPersistKey(key: KEY_Settings, reInit: boolean) {
        this.storage = this.NavSettings.GetUserSetting(key);
        this.storage.LoadValue();
        if (reInit)
            delete this.initialValue;
    }
    /** Add Persistent Settings */
    public Add<T>(name: string, settings: IPersistentSetting<T>, thisArg?: any): () => void;
    /** Add Persistent Settings */
    public Add<T>(name: string, get: () => IPersistentSetting<T>, thisArg?: any): () => void;
    /** Add Persistent Settings */
    public Add<T>(name: string, getState: () => T, applyState: (state: T) => void, thisArg?: any): () => void;

    /** Actual add setting Implementation */
    Add<T>(name: string, arg1: any, arg2: any): () => void {
        let toAdd: IPersistentItem<any>,
            settings = this.settings,
            initialValue = this.initialValue,
            loadedValue = this.loadedValue;
        if (fn.isFunction(arg1)) {
            if (fn.isFunction(arg2))
                toAdd = () => CreateSetting(arg1, arg2);
            else if (arguments.length == 2)
                toAdd = arg1;
            else
                throw new Error("Unhandled Arguments for UserSettingsAccess.Add");
        }
        else if (arguments.length == 2)
            toAdd = arg1;
        else
            toAdd = () => CreateSetting(arg1, arg2);

        var result = settings.addSubItem(name, toAdd);

        // if gets added after Apply; do apply the new item directly ...
        if (initialValue) {
            let added = resolve(toAdd);
            initialValue[name] = added.getState();

            // if alredy loaded, apply
            if (this.isApplied && loadedValue && name in loadedValue) {
                added.apply(loadedValue[name]);
            }
        }
        return result;
    }

    /** Save Registered Values State (if changed from Loaded Value) */
    public Save(sync?: boolean) {
        var saveState = this.settings.getState(this.loadedValue);

        // No storing if unchanged
        var strSave = JSON.stringify(saveState);
        if (this.loadedValue) {
            // Check if loaded hasn't changed
            if (JSON.stringify(this.loadedValue) == strSave) {
                return this.$c.when();
            }
        } else {
            // check if current is initial (
            if (JSON.stringify(this.initialValue) == strSave) {
                this.HasChanges = false;
                return this.$c.when();
            }
        }
        this.HasChanges = true;
        return this.count(this.storage.SaveValue(saveState, undefined, sync === true));
    }
    /** Marker : Current State is Not the Initial/=Default state */
    public HasChanges: boolean;

    /** Force Value Loaded, but do Not apply */
    public ForceLoaded() {
        return this.count(this.storage.LoadValue().then(l => this.loadedValue = l));
    }

    /** Load and apply Registered Values State */
    public Load() {
        // Store State on Load for Reset
        if (!this.initialValue)
            this.initialValue = this.settings.getState();
        var s = this.storage;
        return this.count(this.ForceLoaded().then(loaded => {
            if (s !== this.storage)
                return; // cancel old : Key changed in the meanwhile

            if (this.initialValue) {
                this.HasChanges = !!loaded && (JSON.stringify(loaded) !== JSON.stringify(this.initialValue));
            }

            // isApplied to be set before actuall apply: imp added options during applying get automatically applied;
            this.isApplied = true;
            this.settings.apply(loaded);
        }));
    }

    // ReApply User Settings State
    public ReApply() {
        var applyState = this.settings.getState(this.loadedValue);

        this.settings.apply(applyState);
        this.isApplied = true;
    }

    /** Reset to initial State */
    public Reset() {
        return this.count(this.storage.DeleteValue().then(() => {
            this.settings.apply(this.initialValue);
            delete this.isApplied;
            this.initialValue = this.settings.getState();
            this.loadedValue = undefined;
            this.HasChanges = false;
        }));
    }

    private count<T>(p: PromiseLike<T>) {
        if (++this.OperationCount == 1) {
            this.next(this);
        }
        const afterOp = () => {
            if (--this.OperationCount == 0) {
                this.next(this);
            };
        };
        p.then(afterOp, afterOp);
        return p;
    }
    /** Marker : Counter for currently running async Operations (Load/Save/Reset) */
    public OperationCount = 0;
}

export const DefaultCustomizationConfigName = undefined;

/**
* - Loading and Saving UserSettings
* - Register Events for (Before) Unload (here as Storing usersettings is the mani reason for this)
*/
export class NavSettingsService {
    constructor(private readonly $c: CompatibilityService, private navCustomization: INavCustomization) { }

    /** Storage of Loaded user Settings (avoid multiple callbacks */
    private _Settings = <Record<string, ISetting<any>>>{};

    /** Create User settings access Helper */
    CreateUserSettings(key: KEY_Settings): InfUserSettingsAccess {
        var result = new InfUserSettingsAccess(this, this.$c, key);
        result.ForceLoaded(); // Imp start Load (Performance)
        return result;
    }

    /**  Get User Settings Access Item by Key */
    public GetUserSetting<T = object>(key: KEY_Settings): ISetting<T> {
        return this.GetSetting<T>(key, _baseUserQueryUrl);
    }

    /** Get Global Settings Acces Item By Key */
    public GetGlobalSetting<T>(key: KEY_Settings): ISetting<T> {
        return this.GetSetting<T>(key, _baseGlobalQueryUrl);
    }

    /** Utility to Allow Users to "Ignore" Certain Messages */
    public UserConfirmIgnore(confirm: { Token: string, Area: string }, pipeLine: (state: { HasValue: boolean, CurrValue?: boolean }) => { HasValue: boolean, CurrValue?: boolean } | PromiseLike<{ HasValue: boolean, CurrValue?: boolean }>) {
        var confirmToken = confirm && confirm.Token,
            userConfirmSettings = this.GetUserSetting([confirm.Area, confirm.Token]);

        return userConfirmSettings.LoadValue().then(v => {
            v = v || {};
            var hasValue = confirm.Token in v;
            var state = <{ HasValue: boolean, CurrValue?: boolean }>{
                HasValue: confirmToken in v,

            };
            if (hasValue)
                state.CurrValue = v[confirmToken];

            return this.$c.when(pipeLine(fn.clone(state))).then(info => {
                info = info || state;
                if (state.HasValue != info.HasValue || (state.CurrValue !== info.HasValue)) {
                    if (info.HasValue)
                        v[confirmToken] = info.CurrValue;
                    else
                        delete v[confirmToken];
                    return userConfirmSettings.SaveValue(v).then(() => info);
                }
                return info;
            });
        });
    }

    /**  Get Settings Access Item by Key and Url */
    public GetSetting<T>(key: KEY_Settings, url: string): ISetting<T> {
        var keys = Array.isArray(key) ? key.join("/") : key.toString(),
            configName = this.navCustomization.ConfigName ||  DefaultCustomizationConfigName,
            settingsKey = url + "#" + keys + "#" + configName,
            result = this._Settings[settingsKey];

        if (result)
            return result;

        // Create New Item necessary

        /** Currently Active Loading Setting Promise */
        var currLoad = <PromiseLike<ISettingsInfo>>null;
        /** Currently Active Saving Setting promise */
        var currSave = <PromiseLike<ISettingsInfo>>null;
        /** resolve Current Promise to resolve load data by */
        var resolveLoad = () => currSave || currLoad || (currLoad = this.LoadSettings(keys, url, configName));
        var resolveData = (s: ISettingsInfo) => {
            if (!s)
              return <T>undefined;
            if (s.Content && !(typeof s.Content == "string"))
                return JSON.parse(JSON.stringify(<T><any>s.Content));

            var strContent = <string>s.Content;

            if (!strContent)
                return <T><any>strContent;

            if (result && (typeof s.ModifyAllowed == "boolean"))
                result.DisallowModify = !s.ModifyAllowed;
            // Parse string Content ( should not be necessary anmore)
            try {
                return JSON.parse(strContent);
            } catch (err) {
                throw "Das Laden von Anwendungseinstellungen ist fehlgeschlagen:\nMeldung:" + resolveToError(err, false) +
                "\nSchlüssel:" + JSON.stringify(keys); // + "\nDaten:" + value;
            }
        };
        var modifyDisallowed = () => resolveLoad().then(l => {
            return result.DisallowModify = l && !l.ModifyAllowed;
        });
        return result = this._Settings[settingsKey] = {
            LoadValue: () => resolveLoad().then(resolveData),
            SaveValue: (value: T, ignoreIfNotAllowed?: boolean, sync?: boolean) => {
                if (sync === true) {
                    return currLoad = currSave = this.SaveSettings(keys, value, url, configName, true);
                } else {
                    currSave = modifyDisallowed().then(disAllowed => {
                        if (disAllowed) { // should not be necessayr to explicitly do this on client
                            currSave = null;
                            if (ignoreIfNotAllowed !== true)
                                throw new Error("Änderung nicht erlaubt!");
                            return result.LoadValue();
                        }
                        currSave = null;
                        return currLoad = this.SaveSettings(keys, value, url, configName, sync);
                    });
                    return currSave.then(resolveData);
                }
            },
            DeleteValue: () => {
                var returnValue = modifyDisallowed().then(disAllowed => {
                    if (disAllowed)
                        throw "Löschen nicht erlaubt!";
                    currLoad = currSave = null;
                    return this.DeleteSettings(keys, url);
                });
                return returnValue;
            },
            DisallowModify: undefined
        };
    }

    LoadSettings(key: any, url: string, explicitconfig?: string) {
        var keys = Array.isArray(key) ? key.join("/") : (<string>key).toString();
        var strParams = EncodeQueryData({ configName: explicitconfig || this.navCustomization.ConfigName, key: keys });
        return this.$c.get<ISettingsInfo>(url + strParams, { cache: false }).then(data => data.data);
    }

    SaveSettings(key: any, data: any, url: string, explicitconfig?: string, sync?: boolean): PromiseLike<ISettingsInfo> {
        const keys = Array.isArray(key) ? key.join("/") : (<string>key).toString();
        const strParams = EncodeQueryData({ configName: explicitconfig || this.navCustomization.ConfigName, key: keys });
        const $c = this.$c;
        if (sync === true) {
            // Post Sync (necessary on page unload)
            try {
                let req = new XMLHttpRequest();
                req.open("POST", url + strParams, false);
                req.setRequestHeader("Content-Type", "application/json");
                req.send(JSON.stringify(data));

                return $c.when(<ISettingsInfo>JSON.parse(req.response));
            } catch (err) {
                return $c.reject(err);
            }
        } else {
            return $c.post<ISettingsInfo>(url + strParams, data).then(data => data.data);
        }
    }

    DeleteSettings(key: any, url: string, explicitconfig?: string) {
        var keys = Array.isArray(key) ? key.join("/") : (<string>key).toString();
        var strParams = EncodeQueryData({ configName: explicitconfig || this.navCustomization.ConfigName, deleteKey: keys });
        return this.$c.post<boolean>(url + strParams, undefined).then(d => d.data);
    }

    /** Delete All User Specific Settings for current User */
    DeleteAllSettings(explicitConfig?: string, config?: { mock?: boolean }) {
        var params = EncodeQueryData({ configName: explicitConfig || this.navCustomization.ConfigName, mock: config && (config.mock == true) }); // mock : for test only
        return this.$c.post("/infoma.Web.Nav/NavSettings/DeleteAllUserSettings?" + params, undefined);
    }

    /** Register On BeforeUnload , returns deregistration Function */
    public RegisterOnBeforeUnload(callback: (OuterEv: BeforeUnloadEvent) => void) {
        if (!this._regOnBeforeUnload) {
            var before = <(s: BeforeUnloadEvent) => any>window.onbeforeunload || fn.noop;
            this._regOnBeforeUnload = [];
            window.onbeforeunload = (ev: BeforeUnloadEvent) => {
                before(ev);
                if (window["_NavReload"] !== true) {
                    let curr = this._regOnBeforeUnload;
                    curr.slice(0, curr.length).forEach(item => {
                        if (!ev.defaultPrevented) {
                            item(ev);
                        }
                    });
                }
            };
        }

        this._regOnBeforeUnload.push(callback);
        return () => {
            var curr = this._regOnBeforeUnload;
            var idx = curr.indexOf(callback);
            if (idx >= 0)
                curr.splice(idx, 1);
        };
    }
    private _regOnBeforeUnload: Array<(ev: BeforeUnloadEvent) => any>;

    /** Register On Unload , returns deregistration Function */
    public RegisterOnUnload(callback: () => void) {
        if (!this._regOnUnload) {
            var before = <(e) => any>window.onunload || fn.noop;
            this._regOnUnload = [];
            window.onunload = (e) => {
                before(e);
                var curr = this._regOnUnload || [];
                fn.combine(curr)();
            };
        }
        this._regOnUnload.push(callback);
        return fn.once(() => {
            var curr = this._regOnUnload;
            var idx = curr.indexOf(callback);
            if (idx >= 0)
                curr.splice(idx, 1);
        });
    }
    /** gets set (do not change afterwards) on first Register */
    private _regOnUnload: Array<() => any>;
}

/** Async Container of Items Persisted in Settings (User, Global) */
export class SettingsContainer<T>  {
    private items: T[];
    private loaded: PromiseLike<void>;
    /**
    * @param setting the Settings Container, either as UserSetting or as global setting
    * @param getKey Function to get the unique key for an item in Container
    * @param templateItem {T} A template Item (TODO : used to identify the properties which must be persisted, all other props get ignored in persistence)
     */
    constructor(private $q: CompatibilityService, private setting: ISetting<T[]>, private getKey: (item: T) => string, private templateItem: T) {
        this.items = new Array<T>();
        this.loaded = this.createLoadPromise();
    }

    /** Ensure Items Loaded from Persistence */
    public LoadItems(forceRefresh?: boolean): PromiseLike<T[]> {
        var me = this;
        if (forceRefresh)
            this.loaded = this.afterOps(() => this.createLoadPromise());
        return this.loaded.then(s => me.items);
    }

    /** AddItem to Persistence */
    public AddItem(extAddItem: T): PromiseLike<T> {
        return this.afterOps(() => {
            try {

                if (!extAddItem)
                    throw Error("kein Element zum Hinzufügen!");

                var clonedAddItem = this.cloneToPersistedItem(extAddItem);

                if (this.getContainerItem(clonedAddItem))
                    throw Error("Es existiert bereits eine Element mit diesem Schlüssel (" + this.getKey(clonedAddItem) + ")!");
                var items = this.items.slice();
                items.push(clonedAddItem);
                return this.Update(items).then(data => this.items[this.items.length - 1]);

            } catch (err) {
                return this.$q.reject(err);
            }
        });
    }

    /** Remove Item from Persistence */
    public RemoveItem(item: T) {
        return this.afterOps(() => {
            var key = this.getKey(item);
            var existing = this.items.map((v, idx) => { return this.getKey(v) == key ? idx : -1; }).filter(item => item >= 0);

            if (existing.length == 0)
                throw "Item with key '" + key + "' does not exist!";

            var newItems = this.items.slice();
            newItems.splice(existing[0], 1);
            return this.Update(newItems).then(fn.nullOp);
        });
    }

    /** Replace Corresponding Item (by key!) with new Item */
    public ReplaceItem(existingItem: T, newItem: T) {
        return this.afterOps(() => {
            var toUpdate = this.cloneToPersistedItem(newItem);
            var key = this.getKey(existingItem);
            var toReplace = this.getContainerItem(existingItem);
            if (!toReplace)
                throw "Item with key '" + this.getKey(existingItem) + "' does not exist!";
            var toReplaceIdx = this.items.indexOf(toReplace);
            var checkItem = this.getContainerItem(newItem);
            if (checkItem && checkItem != toReplace) {
                throw Error("ReplaceItem : Item To Replace [" + this.getKey(toReplace) + "] already in Container");
            }

            var newItems = this.items.slice();
            newItems.splice(toReplaceIdx, 1, toUpdate);
            var me = this;
            return this.Update(newItems).then(data => me.getContainerItem(toUpdate));
        });
    }

    /** Set Items Container (Replace All) */
    public SetItems(items: T[]) {
        return this.afterOps(() => {
            items = items.map(item => this.cloneToPersistedItem(item));
            return this.Update(items);
        });
    }

    /**  Update Persistence with container Values (internal : DO NOT Use afterOps inside!) */
    private Update(items: T[]): PromiseLike<T[]> {
        var result = this.setting.SaveValue(items).then(data => {
            if (data == null)
                data = [];

            if (!Array.isArray(data))
                throw Error("Load Persistent User Container : Items is not an array!");

            var clonedItems = data.map(item => this.cloneToPersistedItem(item), this);

            this.items.length = 0;
            this.items.push(...clonedItems);
            return data;
        });

        return result;
    }

    /** ensure a Promise gets resolved only after pending Manipulations finish */
    private afterOps<T>(resolvePromise: () => PromiseLike<T>) {
        var me = this;
        function reset() {
            if (started == me.pendingOp)
                me.pendingOp = undefined;
        }
        function start() {
            try {
                var result = resolvePromise();
                result.then(reset, reset);
                return result;
            } catch (err) {
                reset();
                throw err;
            }
        }
        var started = this.pendingOp = (this.pendingOp || this.$q.when()).then(start, start);
        return started;
    }
    /** currently pending Operation from user perspective (Add, Replace, Remove, Load) */
    private pendingOp: PromiseLike<any>;

    /** get corresponding container item */
    private getContainerItem(item: T): T {
        var key = this.getKey(item);
        return this.items.filter(c => this.getKey(c) == key)[0];
    }

    /** Create a Load Promise for the Items */
    private createLoadPromise() {
        var me = this;
        return this.setting.LoadValue().then(r => {
            if (r == undefined)
                return;
            if (Array.isArray(r)) {
                me.items.push(...r);
            }
        });
    }

    /**  clone Item ensuring only Persistable properties are on object */
    private cloneToPersistedItem(item: T): T {
        var result = {};
        Object.getOwnPropertyNames(this.templateItem).forEach(name => result[name] = item[name]);
        return <T>result;
    }
}

/** Create Typed Settings Apply item
 * @param getFunc get current State to be stored
 * @param applyFunc apply stored state
 */
export function CreateSetting<T>(getFunc: () => T, applyFunc: (item: T) => void): IPersistentSetting<T> {
    var result: IPersistentSetting<T>;
    var subItems: Record<string, IPersistentItem<any>> = {};
    return result = {
        apply: <T>(item: T) => {
            if (item == undefined)
                return;
            applyFunc(<any>item);
            Object.getOwnPropertyNames(subItems).forEach(subItem => {
                var sitem = resolve(subItems[subItem]);
                sitem.apply(item[subItem]);
            });
        },
        getState: <T>(defaultValues?: any) => {
            var result = getFunc();
            Object.getOwnPropertyNames(subItems).forEach(subItem => {
                var item = resolve(subItems[subItem]);
                if (item && fn.isFunction(item.getState)) {
                    var subDefault = (defaultValues && defaultValues[subItem]) || undefined;

                    var subValue = item.getState(subDefault) || subDefault;
                    if (subValue)
                        (result || (result = <any>{}))[subItem] = subValue;
                }
            });
            return result;
        },

        addSubItem: <TSub>(name: string, setting: IPersistentItem<TSub>) => {
            if (setting && name) {
                subItems[name] = setting;
                return fn.once(() => {
                    if (subItems[name] === setting) {
                        delete subItems[name];
                    }
                });
            }
            return fn.nullOp;
        }
    };
}

function resolve<T>(check: T | (() => T)) {
    if (typeof check === "function") {
        return (<() => T>check)();
    }
    return check;
}
